/* eslint-disable react/no-multi-comp */
/* eslint-disable react/jsx-max-props-per-line */
/* eslint-disable react/jsx-sort-props */
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Button, CircularProgress, Stack, Typography } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DialogDefaultV2 } from '../../../components/v2';
import {
  agruparUnidades,
  fetchEquipamentosRequisicao,
  formatDataHistorico,
  labelTipoRepasse,
} from './equipamentosHistorico';

const useEquipamentosRequisicao = (requisicaoId, enabled) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [grupos, setGrupos] = useState([]);

  useEffect(() => {
    if (!enabled || !requisicaoId) return undefined;
    let active = true;
    setLoading(true);
    setError(false);
    fetchEquipamentosRequisicao(requisicaoId)
      .then((data) => {
        if (!active) return;
        setGrupos(data);
        setLoading(false);
      })
      .catch(() => {
        if (!active) return;
        setGrupos([]);
        setError(true);
        setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [requisicaoId, enabled]);

  return { loading, error, grupos };
};

const equipamentoHref = (equipamentoId) => {
  if (!equipamentoId) return null;
  return `/v2/equipamentos?equipamentoId=${encodeURIComponent(equipamentoId)}&p=0&s=10`;
};

const UnidadeHistorico = ({ unidade }) => {
  const detalhe = [unidade.modelo, unidade.numSerie ? `série ${unidade.numSerie}` : null]
    .filter(Boolean)
    .join(' · ');
  const href = equipamentoHref(unidade.equipamentoId);
  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        p: 1.5,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="space-between">
        <Typography variant="body2">
          {unidade.numPatrimonio || 'Sem patrimônio'}
        </Typography>
        {href ? (
          <Button
            size="small"
            variant="text"
            component="a"
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            endIcon={<OpenInNewIcon fontSize="small" />}
            sx={{ px: 0.5, minWidth: 0, '& .MuiButton-endIcon': { ml: 0.5 } }}
          >
            Abrir
          </Button>
        ) : null}
      </Stack>
      {detalhe ? (
        <Typography variant="caption" color="text.secondary" display="block">
          {detalhe}
        </Typography>
      ) : null}
      <Stack spacing={0.75} sx={{ mt: 1 }}>
        {unidade.eventos.map((evento) => (
          <Box key={evento.historicoEquipamentoId || `${evento.tipoRepasse}-${evento.dataHistorico}`}>
            <Typography variant="caption">
              {formatDataHistorico(evento.dataHistorico)}
              {' · '}
              {labelTipoRepasse(evento.tipoRepasse)}
            </Typography>
            {evento.observacao ? (
              <Typography variant="caption" color="text.secondary" display="block">
                {evento.observacao}
              </Typography>
            ) : null}
          </Box>
        ))}
      </Stack>
    </Box>
  );
};

UnidadeHistorico.propTypes = {
  unidade: PropTypes.object.isRequired,
};

export const EquipamentosHistoricoLista = ({ grupos, vazio }) => {
  if (!grupos.length) {
    return (
      <Typography variant="body2" color="text.secondary">
        {vazio}
      </Typography>
    );
  }
  return (
    <Stack spacing={2}>
      {grupos.map((grupo) => {
        const unidades = agruparUnidades(grupo.historico);
        return (
          <Stack key={grupo.requisicaoItemId || grupo.nomeItem} spacing={1}>
            {grupos.length > 1 ? (
              <Typography variant="body2" fontWeight={600}>
                {grupo.nomeItem || 'Equipamento'}
              </Typography>
            ) : null}
            {unidades.length ? (
              unidades.map((unidade) => (
                <UnidadeHistorico
                  key={unidade.equipamentoId || unidade.numPatrimonio}
                  unidade={unidade}
                />
              ))
            ) : (
              <Typography variant="body2" color="text.secondary">
                Nenhum equipamento vinculado a este item.
              </Typography>
            )}
          </Stack>
        );
      })}
    </Stack>
  );
};

EquipamentosHistoricoLista.propTypes = {
  grupos: PropTypes.array,
  vazio: PropTypes.string,
};

EquipamentosHistoricoLista.defaultProps = {
  grupos: [],
  vazio: 'Nenhum equipamento vinculado.',
};

const estadoLista = ({ loading, error, grupos, vazio }) => {
  if (loading) {
    return (
      <Stack direction="row" spacing={1} alignItems="center">
        <CircularProgress size={16} />
        <Typography variant="body2" color="text.secondary">
          Carregando equipamentos…
        </Typography>
      </Stack>
    );
  }
  if (error) {
    return (
      <Typography variant="body2" color="text.secondary">
        Não foi possível carregar os equipamentos.
      </Typography>
    );
  }
  return <EquipamentosHistoricoLista grupos={grupos} vazio={vazio} />;
};

export const EquipamentosHistoricoPainel = ({ requisicaoId }) => {
  const { loading, error, grupos } = useEquipamentosRequisicao(requisicaoId, true);
  return (
    <Stack spacing={1}>
      <Typography variant="body2" fontWeight={600}>
        Histórico de equipamentos
      </Typography>
      {estadoLista({
        loading,
        error,
        grupos,
        vazio: 'Nenhum equipamento vinculado a esta requisição.',
      })}
    </Stack>
  );
};

EquipamentosHistoricoPainel.propTypes = {
  requisicaoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
};

EquipamentosHistoricoPainel.defaultProps = {
  requisicaoId: null,
};

const EquipamentosHistoricoDialog = ({ open, requisicaoId, item, onClose }) => {
  const { loading, error, grupos } = useEquipamentosRequisicao(requisicaoId, open);
  const itemId = item?.id != null ? String(item.id) : null;
  const doItem = itemId
    ? grupos.filter((grupo) => String(grupo.requisicaoItemId) === itemId)
    : grupos;
  const nome = item?.nomeItem || doItem[0]?.nomeItem || 'Equipamento';

  return (
    <DialogDefaultV2
      open={open}
      handlerCancel={onClose}
      title={`Equipamentos · ${nome}`}
      maxWidth="sm"
      fullWidth
      readOnly
    >
      {estadoLista({
        loading,
        error,
        grupos: doItem,
        vazio: 'Nenhum equipamento vinculado a este item.',
      })}
    </DialogDefaultV2>
  );
};

EquipamentosHistoricoDialog.propTypes = {
  open: PropTypes.bool,
  requisicaoId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  item: PropTypes.object,
  onClose: PropTypes.func,
};

EquipamentosHistoricoDialog.defaultProps = {
  open: false,
  requisicaoId: null,
  item: null,
  onClose: () => {},
};

export default EquipamentosHistoricoDialog;
